// Security Services
// Handles security profile operations in users/{userId}/securityProfile subcollection

import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    serverTimestamp,
    DocumentData
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError } from '@/shared/api/errors/ApiError';
import { getFirestoreTimestamps } from '@/shared/utils/api/timestampHelper';
import { hashAnswer, compareAnswerHash } from '@/features/courses/utils/answerHasher';
import ServiceBase from '@/shared/api/base/ServiceBase';

// Extend window for E2E test mocking
declare global {
    interface Window {
        MOCK_SECURITY_VERIFICATION?: boolean;
        MOCK_SECURITY_QUESTION?: SecurityQuestion;
    }
}

// Type definitions
interface SecurityData {
    question1: string;
    answer1: string;
    question2: string;
    answer2: string;
    question3: string;
    answer3: string;
}

interface SecurityProfile {
    question1?: string;
    question2?: string;
    question3?: string;
    answerHash1?: string;
    answerHash2?: string;
    answerHash3?: string;
    failedAttempts?: FailedAttempt[];
    expiresAt?: Date;
    updateHistory?: UpdateHistoryEntry[];
    createdAt?: any;
    updatedAt?: any;
}

interface FailedAttempt {
    timestamp: Date | { toDate: () => Date };
    correctAnswers: number;
    answeredQuestions: number;
}

interface UpdateHistoryEntry {
    timestamp: Date;
    updatedBy: string;
    action: string;
    reason?: string;
}

interface VerificationResult {
    verified: boolean;
    message: string;
    remainingAttempts?: number;
    correctAnswers?: number;
    requiredCorrect?: number;
    answerResults?: AnswerResult[];
    requiresUpdate?: boolean;
}

interface AnswerResult {
    questionNumber: number;
    question: string;
    isCorrect: boolean;
}

interface SecurityQuestion {
    questionNumber: number;
    question: string;
}

interface SecurityQuestionsStatus {
    status: 'not_set' | 'expired' | 'expiring_soon' | 'locked_out' | 'active';
    message: string;
    requiresSetup?: boolean;
    requiresUpdate?: boolean;
    expiresAt?: Date | null;
    daysUntilExpiration?: number;
    shouldWarn?: boolean;
    lockoutUntil?: Date;
    hoursRemaining?: number;
    isLockedOut?: boolean;
    failedAttempts?: number;
}

interface AnswersInput {
    answer1?: string;
    answer2?: string;
    answer3?: string;
    [key: string]: string | undefined;
}

class SecurityService extends ServiceBase {
    constructor() {
        super('SecurityService');
    }

    async getSecurityProfile(userId: string): Promise<SecurityProfile | null> {
        try {
            this.validate.validateUserId(userId);

            const securityRef = doc(db, 'users', userId, 'securityProfile', 'questions');
            const securityDoc = await getDoc(securityRef);

            if (!securityDoc.exists()) {
                return null;
            }

            return {
                ...securityDoc.data()
            } as SecurityProfile;
        } catch (error) {
            this.logError(error, { method: 'getSecurityProfile', userId });
            throw error;
        }
    }

    async setSecurityQuestions(userId: string, securityData: SecurityData): Promise<{ success: boolean; message: string }> {
        try {
            // E2E Test Mock Injection
            const isMocked = typeof window !== 'undefined' && (localStorage.getItem('MOCK_SECURITY_VERIFICATION') || (window as any).MOCK_SECURITY_VERIFICATION !== undefined);
            if (isMocked) {
                console.warn('Returning MOCKED setSecurityQuestions for E2E testing');
                return {
                    success: true,
                    message: 'Security questions set successfully'
                };
            }

            this.validate.validateUserId(userId);
            if (typeof securityData !== 'object' || !securityData) {
                throw new ValidationError('Security data must be a valid object');
            }

            const hash1 = await hashAnswer(securityData.answer1);
            const hash2 = await hashAnswer(securityData.answer2);
            const hash3 = await hashAnswer(securityData.answer3);

            const securityRef = doc(db, 'users', userId, 'securityProfile', 'questions');
            const now = new Date();
            const expirationDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

            const data = {
                question1: securityData.question1,
                answerHash1: hash1,
                question2: securityData.question2,
                answerHash2: hash2,
                question3: securityData.question3,
                answerHash3: hash3,
                failedAttempts: [],
                expiresAt: expirationDate,
                updateHistory: [{
                    timestamp: now,
                    updatedBy: 'self',
                    action: 'initial_setup'
                }],
                ...getFirestoreTimestamps()
            };

            await setDoc(securityRef, data);
            this.log(`Security questions set for user ${userId}`);

            return {
                success: true,
                message: 'Security questions set successfully'
            };
        } catch (error) {
            this.logError(error, { method: 'setSecurityQuestions', userId });
            throw error;
        }
    }

    async updateSecurityQuestions(userId: string, updates: Partial<DocumentData>): Promise<{ success: boolean; message: string }> {
        try {
            // E2E Test Mock Injection
            const isMocked = typeof window !== 'undefined' && (localStorage.getItem('MOCK_SECURITY_VERIFICATION') || (window as any).MOCK_SECURITY_VERIFICATION !== undefined);
            if (isMocked) {
                console.warn('Returning MOCKED updateSecurityQuestions for E2E testing');
                return {
                    success: true,
                    message: 'Security questions updated successfully'
                };
            }

            this.validate.validateUserId(userId);
            if (typeof updates !== 'object' || !updates) {
                throw new ValidationError('Updates must be a valid object');
            }

            const securityRef = doc(db, 'users', userId, 'securityProfile', 'questions');

            const updateData = {
                ...updates,
                updatedAt: serverTimestamp()
            };

            await updateDoc(securityRef, updateData);
            this.log(`Security questions updated for user ${userId}`);

            return {
                success: true,
                message: 'Security questions updated successfully'
            };
        } catch (error) {
            this.logError(error, { method: 'updateSecurityQuestions', userId });
            throw error;
        }
    }

    async verifySecurityAnswer(userId: string, questionNumber: number, providedAnswer: string): Promise<{ verified: boolean; message: string }> {
        try {
            this.validate.validateUserId(userId);

            // E2E Test Mock Injection
            const isMocked = typeof window !== 'undefined' && (localStorage.getItem('MOCK_SECURITY_VERIFICATION') || (window as any).MOCK_SECURITY_VERIFICATION !== undefined);
            if (isMocked) {
                console.warn('Returning MOCKED Security Verification for E2E testing');
                const verified = localStorage.getItem('MOCK_SECURITY_VERIFICATION') === 'true' || (window as any).MOCK_SECURITY_VERIFICATION;
                return {
                    verified: verified,
                    message: verified ? 'Answer verified' : 'Incorrect answer'
                };
            }

            if (typeof questionNumber !== 'number' || questionNumber < 1 || questionNumber > 3) {
                throw new ValidationError('Question number must be between 1 and 3');
            }
            if (typeof providedAnswer !== 'string' || !providedAnswer.trim()) {
                throw new ValidationError('Provided answer must be a non-empty string');
            }

            const securityProfile = await this.getSecurityProfile(userId);

            if (!securityProfile) {
                return {
                    verified: false,
                    message: 'No security questions set'
                };
            }

            const hashKey = `answerHash${questionNumber}` as keyof SecurityProfile;
            const storedHash = securityProfile[hashKey] as string | undefined;

            if (!storedHash) {
                return {
                    verified: false,
                    message: 'Security question not found'
                };
            }

            const isMatch = await compareAnswerHash(providedAnswer, storedHash);

            return {
                verified: isMatch,
                message: isMatch ? 'Answer verified' : 'Incorrect answer'
            };
        } catch (error) {
            this.logError(error, { method: 'verifySecurityAnswer', userId, questionNumber });
            throw error;
        }
    }

    async hasSecurityQuestions(userId: string): Promise<boolean> {
        try {
            this.validate.validateUserId(userId);
            const securityProfile = await this.getSecurityProfile(userId);
            return !!(securityProfile !== null &&
                securityProfile.question1 &&
                securityProfile.answerHash1);
        } catch (error) {
            this.logError(error, { method: 'hasSecurityQuestions', userId });
            throw error;
        }
    }

    async getSecurityQuestionsForRecovery(userId: string): Promise<{ question1?: string; question2?: string; question3?: string } | null> {
        try {
            this.validate.validateUserId(userId);
            const securityProfile = await this.getSecurityProfile(userId);

            if (!securityProfile) {
                return null;
            }

            return {
                question1: securityProfile.question1,
                question2: securityProfile.question2,
                question3: securityProfile.question3
            };
        } catch (error) {
            this.logError(error, { method: 'getSecurityQuestionsForRecovery', userId });
            throw error;
        }
    }

    async verifySecurityAnswers(userId: string, answers: AnswersInput): Promise<VerificationResult> {
        try {
            this.validate.validateUserId(userId);
            if (typeof answers !== 'object' || !answers) {
                throw new ValidationError('Answers must be a valid object');
            }

            const securityProfile = await this.getSecurityProfile(userId);

            if (!securityProfile) {
                return {
                    verified: false,
                    message: 'No security questions set',
                    remainingAttempts: 0
                };
            }

            if (securityProfile.expiresAt && new Date(securityProfile.expiresAt as unknown as string) < new Date()) {
                return {
                    verified: false,
                    message: 'Your security questions have expired. Please update them in settings.',
                    requiresUpdate: true,
                    remainingAttempts: 0
                };
            }

            let correctAnswers = 0;
            const requiredCorrect = 2;
            const answerResults: AnswerResult[] = [];

            for (let i = 1; i <= 3; i++) {
                const answerKey = `answer${i}`;
                const hashKey = `answerHash${i}` as keyof SecurityProfile;
                const questionKey = `question${i}` as keyof SecurityProfile;
                const providedAnswer = answers[answerKey];
                const storedHash = securityProfile[hashKey] as string | undefined;

                if (providedAnswer && storedHash) {
                    const isMatch = await compareAnswerHash(providedAnswer, storedHash);
                    if (isMatch) {
                        correctAnswers++;
                    }
                    answerResults.push({
                        questionNumber: i,
                        question: securityProfile[questionKey] as string,
                        isCorrect: isMatch
                    });
                }
            }

            const verified = correctAnswers >= requiredCorrect;

            if (!verified) {
                const failedAttempt: FailedAttempt = {
                    timestamp: new Date(),
                    correctAnswers,
                    answeredQuestions: answerResults.length
                };

                const failedAttempts = securityProfile.failedAttempts || [];
                failedAttempts.push(failedAttempt);

                const securityRef = doc(db, 'users', userId, 'securityProfile', 'questions');
                await updateDoc(securityRef, { failedAttempts });

                const remainingAttempts = Math.max(0, 2 - failedAttempts.length);

                return {
                    verified: false,
                    correctAnswers,
                    requiredCorrect,
                    remainingAttempts,
                    answerResults,
                    message: remainingAttempts > 0
                        ? `${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining. You got ${correctAnswers}/${answerResults.length} correct.`
                        : 'No attempts remaining. You have been locked out for 24 hours.'
                };
            }

            return {
                verified: true,
                correctAnswers,
                requiredCorrect,
                remainingAttempts: 2,
                answerResults,
                message: 'Security verification successful'
            };
        } catch (error) {
            this.logError(error, { method: 'verifySecurityAnswers', userId });
            throw error;
        }
    }

    async getRandomPersonalSecurityQuestion(userId: string): Promise<SecurityQuestion> {
        try {
            this.validate.validateUserId(userId);

            // E2E Test Mock Injection
            const rawMock = typeof window !== 'undefined' ? (localStorage.getItem('MOCK_SECURITY_QUESTION') || (window as any).MOCK_SECURITY_QUESTION) : undefined;
            if (rawMock) {
                console.warn('Returning MOCKED Security Question for E2E testing');
                return typeof rawMock === 'string' ? JSON.parse(rawMock) : rawMock;
            }

            const securityProfile = await this.getSecurityProfile(userId);

            if (!securityProfile) {
                throw new ValidationError('No security questions set for this user');
            }

            const questions: SecurityQuestion[] = [];
            for (let i = 1; i <= 3; i++) {
                const questionKey = `question${i}` as keyof SecurityProfile;
                const hashKey = `answerHash${i}` as keyof SecurityProfile;

                if (securityProfile[questionKey] && securityProfile[hashKey]) {
                    questions.push({
                        questionNumber: i,
                        question: securityProfile[questionKey] as string
                    });
                }
            }

            if (questions.length === 0) {
                throw new ValidationError('No valid security questions found');
            }

            const randomIndex = Math.floor(Math.random() * questions.length);
            return questions[randomIndex];
        } catch (error) {
            this.logError(error, { method: 'getRandomPersonalSecurityQuestion', userId });
            throw error;
        }
    }

    async resetSecurityQuestionsByAdmin(userId: string, adminId: string): Promise<{ success: boolean; message: string }> {
        try {
            this.validate.validateUserId(userId);
            if (!adminId || typeof adminId !== 'string') {
                throw new ValidationError('Admin ID is required');
            }

            const securityRef = doc(db, 'users', userId, 'securityProfile', 'questions');
            const securityDoc = await getDoc(securityRef);

            if (!securityDoc.exists()) {
                throw new ValidationError('No security questions found for this user');
            }

            const currentData = securityDoc.data();
            const updateHistory = currentData.updateHistory || [];
            updateHistory.push({
                timestamp: new Date(),
                updatedBy: adminId,
                action: 'admin_reset',
                reason: 'User assistance request'
            });

            await updateDoc(securityRef, {
                failedAttempts: [],
                updateHistory,
                updatedAt: serverTimestamp()
            });

            this.log(`Security questions reset by admin ${adminId} for user ${userId}`);

            return {
                success: true,
                message: 'Security questions reset successfully'
            };
        } catch (error) {
            this.logError(error, { method: 'resetSecurityQuestionsByAdmin', userId, adminId });
            throw error;
        }
    }

    async checkSecurityQuestionsStatus(userId: string): Promise<SecurityQuestionsStatus> {
        try {
            this.validate.validateUserId(userId);

            const securityProfile = await this.getSecurityProfile(userId);

            if (!securityProfile) {
                return {
                    status: 'not_set',
                    message: 'Security questions not set',
                    requiresSetup: true
                };
            }

            const now = new Date();
            const expiresAt = securityProfile.expiresAt ? new Date(securityProfile.expiresAt as unknown as string) : null;
            const isExpired = expiresAt && expiresAt < now;

            if (isExpired) {
                const daysExpired = Math.floor((now.getTime() - expiresAt.getTime()) / (1000 * 60 * 60 * 24));
                return {
                    status: 'expired',
                    message: `Security questions expired ${daysExpired} day${daysExpired !== 1 ? 's' : ''} ago`,
                    expiresAt,
                    requiresUpdate: true
                };
            }

            const daysUntilExpiration = expiresAt ? Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;

            if (daysUntilExpiration && daysUntilExpiration <= 30) {
                return {
                    status: 'expiring_soon',
                    message: `Security questions will expire in ${daysUntilExpiration} days`,
                    expiresAt,
                    daysUntilExpiration,
                    shouldWarn: true
                };
            }

            const failedAttempts = securityProfile.failedAttempts || [];
            if (failedAttempts.length >= 2) {
                const lastAttempt = failedAttempts[failedAttempts.length - 1];
                // Note: timestamp might be Firestore Timestamp or JS Date depending on if it's from cache or fresh
                const lastAttemptDate = (lastAttempt.timestamp as { toDate?: () => Date })?.toDate
                    ? (lastAttempt.timestamp as { toDate: () => Date }).toDate()
                    : new Date(lastAttempt.timestamp as unknown as string);
                const lockoutUntil = new Date(lastAttemptDate.getTime() + 24 * 60 * 60 * 1000);

                if (now < lockoutUntil) {
                    const hoursRemaining = Math.ceil((lockoutUntil.getTime() - now.getTime()) / (1000 * 60 * 60));
                    return {
                        status: 'locked_out',
                        message: `Account locked due to failed security attempts. Try again in ${hoursRemaining} hours.`,
                        lockoutUntil,
                        hoursRemaining,
                        isLockedOut: true,
                        failedAttempts: failedAttempts.length
                    };
                }
            }

            return {
                status: 'active',
                message: 'Security questions are active',
                expiresAt,
                failedAttempts: failedAttempts.length
            };
        } catch (error) {
            this.logError(error, { method: 'checkSecurityQuestionsStatus', userId });
            throw error;
        }
    }
}

const securityServices = new SecurityService();
export default securityServices;

// Named exports for backward compatibility
export const getSecurityProfile = (id: string) => securityServices.getSecurityProfile(id);
export const setSecurityQuestions = (id: string, data: SecurityData) => securityServices.setSecurityQuestions(id, data);
export const updateSecurityQuestions = (id: string, updates: Partial<DocumentData>) => securityServices.updateSecurityQuestions(id, updates);
export const verifySecurityAnswer = (id: string, num: number, ans: string) => securityServices.verifySecurityAnswer(id, num, ans);
export const hasSecurityQuestions = (id: string) => securityServices.hasSecurityQuestions(id);
export const getSecurityQuestionsForRecovery = (id: string) => securityServices.getSecurityQuestionsForRecovery(id);
export const verifySecurityAnswers = (id: string, ans: AnswersInput) => securityServices.verifySecurityAnswers(id, ans);
export const getRandomPersonalSecurityQuestion = (id: string) => securityServices.getRandomPersonalSecurityQuestion(id);
export const resetSecurityQuestionsByAdmin = (id: string, adminId: string) => securityServices.resetSecurityQuestionsByAdmin(id, adminId);
export const checkSecurityQuestionsStatus = (id: string) => securityServices.checkSecurityQuestionsStatus(id);
